import { transformToNestedData } from '@app/features/Resource/ResourceList/utils/transformToNestedData';
import useWorkspace from '@app/features/Workspace/store/hooks/useWorkspace';
import { vars } from '@app/shared/themes/vars';
import Table, { useTable } from '@app/shared/ui-kit/organisms/Table';
import {
  ResourceIndexView,
  SortDirection,
  useResourcesIndexViewQuery,
} from '@app/types/blackroom-api';
import {
  MRT_RowSelectionState,
  MRT_TableOptions,
  MRT_RowVirtualizer,
} from 'mantine-react-table';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import baseConfig from './base-config';
import useColumns from './useColumns';
import getAllDescendantIds from './utils/getAllDescendantIds';
import * as styles from './styles.css';

function ResourceSelector({
  setSelectedResources,
  selectedResources,
  excludeResourceIds = [],
}: {
  setSelectedResources: Dispatch<SetStateAction<ResourceIndexView[]>>;
  selectedResources: ResourceIndexView[];
  excludeResourceIds?: string[];
}) {
  const workspace = useWorkspace();

  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>(
    selectedResources.reduce((acc, resource) => {
      acc[resource.id] = true;
      return acc;
    }, {} as MRT_RowSelectionState),
  );
  const [globalFilter, setGlobalFilter] = useState<string>('');
  // Track the previous selectedResources to detect external changes
  const prevSelectedResourcesRef =
    useRef<ResourceIndexView[]>(selectedResources);
  const [highlightedRowId, setHighlightedRowId] = useState<string | null>(null);
  const rowVirtualizerInstanceRef = useRef<MRT_RowVirtualizer>(null);
  const highlightTimeoutRef = useRef<NodeJS.Timeout>();

  const { data, loading: isFetching } = useResourcesIndexViewQuery({
    variables: {
      filter: {
        workspaceId: workspace.id,
      },
      sorter: {
        index: SortDirection.Asc,
      },
    },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  // Transform the data once when it changes, excluding already associated resources
  const nestedData = useMemo(() => {
    const excludeSet = new Set(excludeResourceIds);
    const filteredData = (data?.resourcesIndexView ?? []).filter(
      (r) => !excludeSet.has(r.id),
    ) as ResourceIndexView[];

    return transformToNestedData(filteredData);
  }, [data, excludeResourceIds]);

  const getFolderContentIds = useCallback(
    (resourceId: string) =>
      getAllDescendantIds(resourceId, nestedData).filter(
        (id) => id !== resourceId,
      ),
    [nestedData],
  );

  const handleToggleFolderContentSelection = useCallback(
    (resourceId: string) => {
      const contentIds = getFolderContentIds(resourceId);

      if (contentIds.length === 0) {
        return;
      }

      setRowSelection((prev) => {
        const nextSelection = { ...prev, [resourceId]: true };
        const isAllContentSelected = contentIds.every((id) => prev[id]);

        contentIds.forEach((id) => {
          if (isAllContentSelected) {
            delete nextSelection[id];
          } else {
            nextSelection[id] = true;
          }
        });

        return nextSelection;
      });
    },
    [getFolderContentIds],
  );

  const clearSearchAndScrollToResource = useCallback(
    (resourceId: string) => {
      if (!globalFilter) {
        return;
      }

      setGlobalFilter('');

      setTimeout(() => {
        const rowIndex =
          data?.resourcesIndexView.findIndex((r) => r.id === resourceId) ?? -1;

        // Scroll to element
        if (rowIndex !== -1) {
          rowVirtualizerInstanceRef.current?.scrollToIndex(rowIndex, {
            align: 'center',
          });

          // Set the highlighted row
          setHighlightedRowId(resourceId);

          // Clear any existing timeout
          if (highlightTimeoutRef.current) {
            clearTimeout(highlightTimeoutRef.current);
          }

          // Clear the highlight after 1.5 seconds
          highlightTimeoutRef.current = setTimeout(() => {
            setHighlightedRowId(null);
          }, 1500);
        }
      }, 0);
    },
    [data?.resourcesIndexView, globalFilter],
  );

  // Sync rowSelection -> selectedResources (internal changes)
  useEffect(() => {
    const selectedResourcesFromSelection =
      data?.resourcesIndexView?.filter((r) => rowSelection[r.id]) ?? [];
    setSelectedResources(selectedResourcesFromSelection);
    // Update ref after syncing to parent
    prevSelectedResourcesRef.current = selectedResourcesFromSelection;
  }, [rowSelection, data, setSelectedResources]);

  // Sync external changes to selectedResources back to rowSelection
  useEffect(() => {
    // Compare with previous value to detect external changes
    const prevIds = new Set(prevSelectedResourcesRef.current.map((r) => r.id));
    const newIds = new Set(selectedResources.map((r) => r.id));

    // Check if this is an external change (not caused by our own setSelectedResources)
    const isExternalChange =
      prevIds.size !== newIds.size ||
      [...prevIds].some((id) => !newIds.has(id)) ||
      [...newIds].some((id) => !prevIds.has(id));

    if (isExternalChange) {
      // Update ref first to prevent re-triggering
      prevSelectedResourcesRef.current = selectedResources;
      setRowSelection(
        selectedResources.reduce((acc, resource) => {
          acc[resource.id] = true;
          return acc;
        }, {} as MRT_RowSelectionState),
      );
    }
  }, [selectedResources]);

  // Scroll to top when starting to search
  useEffect(() => {
    if (globalFilter && globalFilter.length > 0) {
      if (rowVirtualizerInstanceRef.current) {
        rowVirtualizerInstanceRef.current.scrollToIndex(0, {
          align: 'start',
        });
      }
    }
  }, [globalFilter]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
    };
  }, []);

  // Table
  const columnDefs = useColumns({
    globalFilter,
    onClickResourceName: clearSearchAndScrollToResource,
    onToggleFolderContentSelection: handleToggleFolderContentSelection,
    getFolderContentIds,
    rowSelection,
  });
  const listConfig: MRT_TableOptions<ResourceIndexView> = {
    ...baseConfig,
    columns: columnDefs,
    data: nestedData,
    mantineTableContainerProps: {
      style: {
        scrollbarColor: `${vars.colors.blackroomBlack} ${vars.colors.blackroomWhite}`,
        scrollbarWidth: 'thin',
        height: '100%',
      },
    },
    rowVirtualizerInstanceRef: rowVirtualizerInstanceRef,
    onGlobalFilterChange: setGlobalFilter,
    mantineTopToolbarProps: {
      className: styles.toolbar,
    },
    mantineSearchTextInputProps: {
      styles: {
        root: {
          width: '100%',
        },
        input: {
          flex: 1,
        },
      },
    },
    displayColumnDefOptions: {
      'mrt-row-select': {
        header: '',
      },
      'mrt-row-expand': {
        header: undefined,
      },
    },
    mantineTableBodyRowProps: ({ row }) => ({
      style: {
        height: 50,
        backgroundColor:
          highlightedRowId === row.original.id ? '#e6f3ff' : undefined,
        transition: 'background-color 0.3s ease',
      },
    }),
    enablePagination: false,
    enableBottomToolbar: false,
    enableRowVirtualization: true,
    enableFullScreenToggle: false,
    enableRowOrdering: false,
    enableSelectAll: true,
    getRowCanExpand: (row) => row.subRows.length > 0,
    enableRowSelection: true,
    enableSubRowSelection: false,
    rowVirtualizerOptions: { overscan: 15 },
    enableGlobalFilterModes: false,
    positionGlobalFilter: 'left',
    filterFromLeafRows: true,
    positionToolbarAlertBanner: 'none',
    enableToolbarInternalActions: false,
    onRowSelectionChange: setRowSelection,
    state: {
      showGlobalFilter: true,
      globalFilter,
      isLoading: isFetching,
      rowSelection: rowSelection,
      columnOrder: ['mrt-row-select', 'index', 'mrt-row-expand'],
      sorting: [{ id: 'index', desc: false }],
      expanded: true,
      columnVisibility: { 'mrt-row-expand': false },
    },
  };

  const table = useTable(listConfig);

  return <Table table={table} />;
}

export default ResourceSelector;
